import { useToast } from "@/components/ui/use-toast.ts";
import { fetchApi } from "@/lib/api/index.ts";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";

export enum ReferralType {
  OUTPATIENT_THERAPY = "OUTPATIENT_THERAPY",
  INTENSIVE_OUTPATIENT = "INTENSIVE_OUTPATIENT",
  PARTIAL_HOSPITALIZATION = "PARTIAL_HOSPITALIZATION",
  MEDICAL_PROVIDER = "MEDICAL_PROVIDER",
  EMERGENCY_SERVICES = "EMERGENCY_SERVICES",
  CRISIS_HOTLINE = "CRISIS_HOTLINE",
  SCHOOL_COUNSELOR = "SCHOOL_COUNSELOR",
  FAMILY_SERVICES = "FAMILY_SERVICES",
  SUBSTANCE_ABUSE = "SUBSTANCE_ABUSE",
  OTHER = "OTHER",
}

export interface ReferralMetadata {
  teenId: string;
  referralDate: string;
  referralRecipient: string;
  referralType: ReferralType;
  internalNote?: string;
}

export interface Referral {
  id: string;
  title: string;
  content: string;
  metadata: ReferralMetadata;
  type: "REFERRAL";
  createdAt: string;
  updatedAt: string;
  mentorshipId: string;
  createdBy: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
  sharedWith?: string[];
}

export interface ReferralsResponse {
  referrals: Referral[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CreateReferralData {
  teenId: string;
  referralDate: string;
  referralRecipient: string;
  referralType: ReferralType;
  internalNote?: string;
}

// Helper function to format referral type for display
export const formatReferralType = (type: ReferralType): string => {
  return type
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

// Helper function to calculate days since referral
export const getDaysSinceReferral = (referralDate: string): number => {
  const today = new Date();
  const refDate = new Date(referralDate);
  // Zero out the time for both dates to compare only the date part
  today.setHours(0, 0, 0, 0);
  refDate.setHours(0, 0, 0, 0);
  if (isNaN(refDate.getTime())) {
    // Graceful fallback, should not happen as per user
    return 0;
  }
  const diffTime = today.getTime() - refDate.getTime();
  // Floor division to get full days difference
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

export interface SupervisedMentorship {
  mentorship_id: string;
  mentee_id: string;
  mentee_name: string;
  mentee_email: string;
  mentee_profile_picture_url?: string;
  mentor_id: string;
  mentor_name: string;
  mentorship_deactivated: boolean;
}

// Hook to get supervised mentorships for creating referrals
// Returns mentorships for superadmins/supervised mentorships for admins
// Can optionally include deactivated teens if includeDeactivated=true
export const useSupervisedMentorships = (includeDeactivated = false) => {
  return useQuery({
    queryKey: ["supervised-mentorships", includeDeactivated],
    queryFn: async (): Promise<{ mentorships: SupervisedMentorship[] }> => {
      // The backend automatically returns appropriate data based on user role:
      // - Superadmins: All mentorships (active + deactivated if includeDeactivated=true)
      // - Admins/Supervisors: Only supervised mentorships (active + deactivated if includeDeactivated=true)
      const response = await fetchApi<{ mentorships: SupervisedMentorship[] }>({
        url: "/v2/users/me/supervised-mentorships",
        params: {
          includeDeactivated: includeDeactivated ? "true" : undefined,
        },
        method: "GET",
      });
      return response.data;
    },
  });
};

export const useReferrals = (
  page: number = 1,
  limit: number = 20,
  search?: string,
  teenId?: string,
  dateFrom?: string,
  dateTo?: string,
  type?: string,
) => {
  return useQuery({
    queryKey: [
      "referrals",
      page,
      limit,
      search,
      teenId,
      dateFrom,
      dateTo,
      type,
    ],
    queryFn: async (): Promise<ReferralsResponse> => {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("limit", limit.toString());

      if (search) params.append("search", search);
      if (teenId) params.append("teenId", teenId);
      if (dateFrom) params.append("dateFrom", dateFrom);
      if (dateTo) params.append("dateTo", dateTo);
      if (type) params.append("type", type);

      const response = await fetchApi<ReferralsResponse>({
        url: `/v2/referrals?${params.toString()}`,
        method: "GET",
      });
      return response.data;
    },
    placeholderData: keepPreviousData,
  });
};

export const exportReferrals = async (filters?: {
  search?: string;
  teenId?: string;
  dateFrom?: string;
  dateTo?: string;
  type?: string;
}): Promise<ReferralsResponse> => {
  const params = new URLSearchParams();
  params.append("page", "1");
  params.append("limit", "10000");

  if (filters?.search) params.append("search", filters.search);
  if (filters?.teenId) params.append("teenId", filters.teenId);
  if (filters?.dateFrom) params.append("dateFrom", filters.dateFrom);
  if (filters?.dateTo) params.append("dateTo", filters.dateTo);
  if (filters?.type) params.append("type", filters.type);

  const response = await fetchApi<ReferralsResponse>({
    url: `/v2/referrals?${params.toString()}`,
    method: "GET",
  });
  return response.data;
};

export const useCreateReferral = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      data: CreateReferralData,
    ): Promise<{ referral: Referral }> => {
      const response = await fetchApi<{ referral: Referral }>({
        url: "/v2/referrals",
        method: "POST",
        data,
      });
      return response.data;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Referral created successfully",
      });
      // Invalidate referrals queries to refetch data
      queryClient.invalidateQueries({ queryKey: ["referrals"] });
    },
    onError: (error: AxiosError<{ error: string }>) => {
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to create referral",
        variant: "destructive",
      });
    },
  });
};
